import { useState, useMemo } from "react";
import { Check, Loader2, AlertTriangle, X, Plus, Trash2, Info, FileText, Paperclip, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhotoCapture } from "@/components/PhotoCapture";
import { fmtTemp } from "@/lib/format";

const COMP = {
  compliance_yesno: ["Sí", "No"],
  cumple: ["Cumple", "No cumple"],
  compliance_yesnona: ["Sí", "No", "N/A"],
  cumple_na: ["Cumple", "No cumple", "N/A"],
};

const K = (sid, row, qid) => `${sid}::${row}::${qid}`;

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = () => reject(new Error("No se pudo leer el archivo"));
  r.readAsDataURL(file);
});

// Single source of truth for rendering/filling a planilla. Used by PlanillaFill (real
// submission) and PlanillaPreview (temporary, no side effects) so both stay in sync.
export function PlanillaForm({ template, mode = "fill", embedded = false, submitting = false, onFinish, onClose, topSlot }) {
  const tpl = template || {};
  const sections = tpl.sections || [];
  const preview = mode === "preview";

  const [answers, setAnswers] = useState({});
  const [photos, setPhotos] = useState({});
  const [files, setFiles] = useState({});
  const [obs, setObs] = useState({});
  const [caNote, setCaNote] = useState("");
  const [rows, setRows] = useState(() => {
    const init = {};
    sections.forEach((s) => { init[s.id] = s.repeatable ? [] : [0]; });
    return init;
  });

  const setA = (k, v) => setAnswers((p) => ({ ...p, [k]: v }));
  const addRow = (sid) => setRows((p) => ({ ...p, [sid]: [...(p[sid] || [0]), ((p[sid] || [0]).at(-1) ?? -1) + 1] }));
  const removeRow = (sid, row) => setRows((p) => ({ ...p, [sid]: (p[sid] || [0]).filter((r) => r !== row) }));

  const outOfRange = (q, v) => {
    if (!["number", "temperature"].includes(q.type) || v === "" || v == null) return false;
    const n = Number(String(v).replace(",", "."));
    return (q.min != null && n < q.min) || (q.max != null && n > q.max);
  };

  const { total, answeredCount, anyIncident } = useMemo(() => {
    let total = 0, answeredCount = 0, anyIncident = false;
    sections.forEach((s) => {
      (rows[s.id] || [0]).forEach((r) => {
        s.questions.forEach((q) => {
          if (q.type === "instruction") return;
          const v = answers[K(s.id, r, q.id)];
          if (q.required && !["photo", "file"].includes(q.type)) {
            total++;
            if (v !== undefined && v !== "") answeredCount++;
          }
          if (outOfRange(q, v) || ["No", "No cumple"].includes(v)) anyIncident = true;
        });
      });
    });
    return { total, answeredCount, anyIncident };
  }, [sections, rows, answers]);

  const handlePrimary = () => {
    if (preview) { onClose?.(); return; }
    for (const s of sections) {
      for (const r of (rows[s.id] || [0])) {
        for (const q of s.questions) {
          if (q.observation_on && answers[K(s.id, r, q.id)] === q.observation_on && !(obs[K(s.id, r, q.id)] || "").trim()) {
            toast.error(`Observación requerida: ${q.label}`);
            return;
          }
        }
      }
    }
    const ansArr = [];
    sections.forEach((s) => (rows[s.id] || [0]).forEach((r) => s.questions.forEach((q) => {
      if (q.type === "instruction") return;
      const k = K(s.id, r, q.id);
      const f = files[k];
      ansArr.push({ question_id: q.id, section_id: s.id, row_index: r,
        value: answers[k] ?? null, observation: obs[k] || null, photo_base64: photos[k] || null,
        file_base64: f?.base64 || null, file_name: f?.name || null, file_mime: f?.mime || null });
    })));
    onFinish?.({ answers: ansArr, anyIncident, caNote });
  };

  const renderQuestion = (q, sid, row) => {
    const k = K(sid, row, q.id);
    const val = answers[k];
    if (q.type === "instruction") {
      const warn = q.style === "warning";
      return (
        <div key={k} data-testid={`instruction-${k}`} className={`rounded-2xl border p-4 ${warn ? "border-amber-200 bg-amber-50" : "border-blue-200 bg-blue-50"}`}>
          <div className="flex items-start gap-2">
            {warn ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> : <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]" />}
            <div>
              {q.label && <p className={`font-semibold ${warn ? "text-amber-800" : "text-[#1D4ED8]"}`}>{q.label}</p>}
              {q.help_text && <p className={`mt-0.5 whitespace-pre-line text-sm ${warn ? "text-amber-700" : "text-[#1e3a8a]"}`}>{q.help_text}</p>}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div key={k} data-testid={`q-${k}`} className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
        <p className="font-medium text-[#153D35]">{q.label}{q.required && <span className="text-red-500"> *</span>}</p>
        {COMP[q.type] && (
          <div className={`mt-3 grid gap-2 ${COMP[q.type].length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
            {COMP[q.type].map((opt) => (
              <button key={opt} data-testid={`opt-${k}-${opt}`} onClick={() => setA(k, opt)}
                className={`h-12 rounded-xl text-sm font-semibold transition-colors ${val === opt ? (["No", "No cumple"].includes(opt) ? "bg-red-600 text-white" : opt === "N/A" ? "bg-slate-500 text-white" : "bg-emerald-600 text-white") : "bg-slate-100 text-[#153D35]"}`}>{opt}</button>
            ))}
          </div>
        )}
        {["number", "temperature"].includes(q.type) && (
          <div className="mt-3">
            <Input data-testid={`num-${k}`} inputMode="decimal" value={val ?? ""} onChange={(e) => setA(k, e.target.value)}
              className={`h-12 tnum text-lg ${outOfRange(q, val) ? "border-red-400" : ""}`} placeholder={q.unit ? `°${q.unit}` : ""} />
            {q.min != null && <p className="mt-1 text-xs text-slate-400">Rango {fmtTemp(q.min, q.unit)}–{fmtTemp(q.max, q.unit)}</p>}
            {outOfRange(q, val) && <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-red-600"><AlertTriangle className="h-3.5 w-3.5" /> Fuera de rango</p>}
          </div>
        )}
        {q.type === "textarea" && <Textarea data-testid={`txt-${k}`} value={val ?? ""} onChange={(e) => setA(k, e.target.value)} className="mt-3" />}
        {q.type === "text" && <Input data-testid={`txt-${k}`} value={val ?? ""} onChange={(e) => setA(k, e.target.value)} className="mt-3 h-12" />}
        {q.type === "date" && <Input type="date" value={val ?? ""} onChange={(e) => setA(k, e.target.value)} className="mt-3 h-12" />}
        {q.type === "time" && <Input type="time" value={val ?? ""} onChange={(e) => setA(k, e.target.value)} className="mt-3 h-12" />}
        {q.type === "select" && (
          <Select value={val ?? ""} onValueChange={(v) => setA(k, v)}>
            <SelectTrigger data-testid={`sel-${k}`} className="mt-3 h-12"><SelectValue placeholder="Selecciona" /></SelectTrigger>
            <SelectContent>{(q.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {q.type === "file" && (
          <div className="mt-3">
            {files[k] ? (
              <div className="flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                <FileText className="h-5 w-5 shrink-0 text-[#2563EB]" />
                <span className="min-w-0 flex-1 truncate text-sm text-[#153D35]">{files[k].name}</span>
                <button data-testid={`file-remove-${k}`} onClick={() => setFiles((p) => ({ ...p, [k]: null }))} className="grid h-8 w-8 place-items-center rounded-full hover:bg-slate-200"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] py-4 text-sm text-[#68756F] hover:border-[#2563EB] hover:text-[#2563EB]">
                <Paperclip className="h-4 w-4" /> Adjuntar documento (PDF, imagen, Office)
                <input type="file" data-testid={`file-input-${k}`} className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,image/jpeg,image/png,image/webp,application/pdf"
                  onChange={async (e) => {
                    const file = e.target.files?.[0]; e.target.value = "";
                    if (!file) return;
                    if (file.size > 10 * 1024 * 1024) { toast.error("El archivo supera 10 MB"); return; }
                    try { const d = await readFileAsDataUrl(file); setFiles((p) => ({ ...p, [k]: { base64: d, name: file.name, mime: file.type || "application/octet-stream" } })); }
                    catch (err) { toast.error(err.message); }
                  }} />
              </label>
            )}
          </div>
        )}
        {(q.photo_required || q.type === "photo" || (outOfRange(q, val) && ["number", "temperature"].includes(q.type))) && (
          <div className="mt-3">
            {photos[k] ? (
              <div className="relative"><img src={photos[k]} alt="" className="aspect-[4/3] w-full rounded-xl object-cover" />
                <button onClick={() => setPhotos((p) => ({ ...p, [k]: null }))} className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-white/90"><X className="h-4 w-4" /></button></div>
            ) : <PhotoCapture onCapture={(d) => setPhotos((p) => ({ ...p, [k]: d }))} testidPrefix={`qphoto-${k}`} />}
            {q.photo_required && <p className="mt-1 text-xs text-slate-400">Fotografía requerida</p>}
          </div>
        )}
        {(q.observation_on && val === q.observation_on) && (
          <Textarea data-testid={`obs-${k}`} value={obs[k] || ""} onChange={(e) => setObs((p) => ({ ...p, [k]: e.target.value }))} className="mt-3 border-amber-300" placeholder="Observación obligatoria *" />
        )}
      </div>
    );
  };

  const bodyClass = embedded ? "min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-4" : "";
  const outerClass = embedded ? "flex h-full min-h-0 flex-col" : "mx-auto max-w-md px-4 pb-28 pt-4";
  const footerClass = embedded
    ? "sticky bottom-0 border-t bg-white/95 px-4 py-3 backdrop-blur"
    : "fixed inset-x-0 bottom-0 z-40 border-t bg-white/95 px-4 py-3 backdrop-blur";

  return (
    <div className={outerClass}>
      <div className={bodyClass}>
        {topSlot}
        <div className="mb-4 rounded-2xl border border-[#E2E8F0] bg-white p-4">
          <p className="font-display text-lg font-semibold text-[#153D35]">{tpl.name || "Planilla"}</p>
          <p className="text-sm text-[#68756F]">{tpl.category}{tpl.target_time ? ` · ${tpl.target_time}` : ""}</p>
        </div>

        {sections.map((sec) => {
          const singular = sec.singular_label || "Registro";
          const secRows = rows[sec.id] || [0];
          return (
            <div key={sec.id} className="mb-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">{sec.title}</p>
              {sec.repeatable ? (
                <div className="space-y-4">
                  {secRows.length === 0 && (
                    <p className="rounded-xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-4 text-center text-sm text-slate-400">
                      Agrega al menos un {singular.toLowerCase()} para registrar.
                    </p>
                  )}
                  {secRows.map((r, idx) => (
                    <div key={r} data-testid={`row-${sec.id}-${r}`} className="rounded-2xl border border-blue-100 bg-blue-50/40 p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-sm font-semibold text-[#1D4ED8]">{singular} {idx + 1}</span>
                        {secRows.length > 1 && (
                          <button data-testid={`remove-row-${sec.id}-${r}`} onClick={() => removeRow(sec.id, r)} className="inline-flex items-center gap-1 text-xs text-red-500"><Trash2 className="h-3.5 w-3.5" /> Quitar</button>
                        )}
                      </div>
                      <div className="space-y-3">{sec.questions.map((q) => renderQuestion(q, sec.id, r))}</div>
                    </div>
                  ))}
                  <Button variant="outline" data-testid={`add-row-${sec.id}`} onClick={() => addRow(sec.id)} className="w-full rounded-xl border-dashed">
                    <Plus className="mr-2 h-4 w-4" /> Agregar {singular.toLowerCase()}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">{sec.questions.map((q) => renderQuestion(q, sec.id, 0))}</div>
              )}
            </div>
          );
        })}

        {anyIncident && (
          <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="mb-2 text-sm font-semibold text-amber-700">Acción correctiva</p>
            <Textarea data-testid="planilla-ca-note" value={caNote} onChange={(e) => setCaNote(e.target.value)} placeholder="Describe la acción tomada" />
          </div>
        )}
      </div>

      <div className={footerClass}>
        <div className={embedded ? "flex items-center gap-3" : "mx-auto flex max-w-md items-center gap-3"}>
          <div className="min-w-0 flex-1">
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-[#2563EB]" style={{ width: `${total ? (answeredCount / total) * 100 : 0}%` }} /></div>
            <p className="mt-1 text-xs text-[#68756F]">{answeredCount} de {total} completados{preview ? " · vista previa" : ""}</p>
          </div>
          {preview ? (
            <Button data-testid="preview-close-btn" onClick={handlePrimary} className="h-12 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">
              <Eye className="mr-1.5 h-4 w-4" /> Cerrar vista previa
            </Button>
          ) : (
            <Button data-testid="planilla-submit-btn" disabled={submitting} onClick={handlePrimary} className="h-12 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Check className="mr-1.5 h-4 w-4" /> Finalizar</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
